import React, { useState } from "react";
import {
  BaseRecord,
  IResourceComponentsProps,
  useCreate,
  useInvalidate,
  useTranslate,
  useUpdate,
} from "@refinedev/core";
import { DateField, DeleteButton, List, useTable } from "@refinedev/antd";
import {
  Avatar,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { GlassCard } from "../../shared/components";
import { useAvatarPalette } from "../../shared/styles";
import { getAvatarColor, getInitialsFromName } from "../../shared/utils";

const { Text } = Typography;

/**
 * Account groups segment customer accounts so product rules can condition on
 * them. Deliberately a table with one inline modal — the same shape as
 * user-groups, and far less machinery than the rule cards need.
 *
 * There is no member picker: accounts point at a group, not the reverse, and
 * the API has no bulk-assign endpoint. Assignment happens on the account.
 */
export const AccountGroupList: React.FC<
  IResourceComponentsProps & { hideHeader?: boolean }
> = ({ hideHeader }) => {
  const _t = useTranslate();
  const avatarPalette = useAvatarPalette();
  const invalidate = useInvalidate();
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<BaseRecord | null>(null);

  const { tableProps, tableQueryResult } = useTable({
    syncWithLocation: true,
    resource: "account-groups",
  });

  const { mutate: createGroup, isLoading: isCreating } = useCreate();
  const { mutate: updateGroup, isLoading: isUpdating } = useUpdate();

  const handleOpenCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (record: BaseRecord) => {
    setEditingRecord(record);
    form.setFieldsValue({
      name: (record as any).name,
      description: (record as any).description,
      isActive: (record as any).isActive ?? true,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const afterMutation = () => {
    handleCloseModal();
    invalidate({ resource: "account-groups", invalidates: ["list"] });
    tableQueryResult?.refetch();
  };

  const handleFormFinish = (values: any) => {
    const payload = {
      name: values.name,
      description: values.description || null,
      isActive: values.isActive ?? true,
    };

    if (editingRecord) {
      updateGroup(
        { resource: "account-groups", id: editingRecord.id, values: payload },
        { onSuccess: afterMutation },
      );
      return;
    }

    createGroup(
      { resource: "account-groups", values: payload },
      { onSuccess: afterMutation },
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      <List
        resource="account-groups"
        title={
          hideHeader ? false : _t("account-groups.titles.list", "Account groups")
        }
        breadcrumb={hideHeader ? false : undefined}
        headerButtons={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenCreateModal}
          >
            {_t("buttons.create", "Create")}
          </Button>
        }
      >
        <style>{`
          .account-groups-list-table-view .ant-table { background: transparent !important; }
          .account-groups-list-table-view .ant-table-thead > tr > th { background: transparent !important; }
          .account-groups-list-table-view .ant-table-tbody > tr > td { background: transparent !important; }
        `}</style>
        <GlassCard className="account-groups-list-table-view !p-0 overflow-hidden">
          <Table {...tableProps} rowKey="id">
            <Table.Column
              title={_t("account-groups.fields.name", "Group")}
              key="group"
              width={320}
              render={(_value, record: any) => (
                <Space>
                  <Avatar
                    shape="square"
                    size={44}
                    style={{
                      backgroundColor: getAvatarColor(record.id, avatarPalette),
                      fontWeight: 600,
                    }}
                  >
                    {getInitialsFromName(record.name)}
                  </Avatar>
                  <div>
                    <Text strong>{record.name}</Text>
                    {record.description && (
                      <>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {record.description}
                        </Text>
                      </>
                    )}
                  </div>
                </Space>
              )}
            />
            <Table.Column
              dataIndex="isActive"
              title={_t("account-groups.fields.isActive", "Active")}
              width={140}
              render={(value: boolean) => (
                <Tag color={value ? "green" : "default"}>
                  {value
                    ? _t("account-groups.fields.isActiveValues.true", "Active")
                    : _t("account-groups.fields.isActiveValues.false", "Inactive")}
                </Tag>
              )}
            />
            <Table.Column
              dataIndex="createdAt"
              title={_t("fields.createdAt", "Created at")}
              render={(value: string) =>
                value ? <DateField value={value} format="DD/MM/YYYY HH:mm" /> : "-"
              }
            />
            <Table.Column
              title={_t("table.actions", "Actions")}
              dataIndex="actions"
              width={120}
              render={(_value, record: BaseRecord) => (
                <Space>
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    aria-label={_t("buttons.edit", "Edit")}
                    onClick={() => handleOpenEditModal(record)}
                  />
                  <DeleteButton
                    resource="account-groups"
                    hideText
                    size="small"
                    recordItemId={record.id}
                    onSuccess={() => tableQueryResult?.refetch()}
                  />
                </Space>
              )}
            />
          </Table>
        </GlassCard>
      </List>

      <Modal
        title={
          editingRecord
            ? _t("account-groups.titles.edit", "Edit group")
            : _t("account-groups.titles.create", "Create group")
        }
        open={isModalOpen}
        onCancel={handleCloseModal}
        width={600}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={handleCloseModal}>
            {_t("buttons.cancel", "Cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isCreating || isUpdating}
            onClick={() => form.submit()}
          >
            {_t("buttons.save", "Save")}
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormFinish}
          initialValues={{ isActive: true }}
        >
          <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
            <Col xs={24}>
              <Form.Item
                label={_t("account-groups.fields.name", "Name")}
                name="name"
                rules={[
                  {
                    required: true,
                    message: _t("forms.errors.required", "This field is required"),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label={_t("account-groups.fields.description", "Description")}
                name="description"
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label={_t("account-groups.fields.isActive", "Active")}
                name="isActive"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};
